import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Grid,
    IconButton,
} from "@mui/material";
// import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import * as yup from "yup";
import { createRoleAction, editRoleAction, getModuleListAction } from "../../Actions/RoleActions";
import { MyCloseIcon } from "../../Assets/svg/MyIcons";
import { FRONTEND_MESSAGES } from "../../utils/messages";

const { VALIDATION } = FRONTEND_MESSAGES;

const AddEditRolesModal = ({
    open,
    handleClose,
    roleDataById,
    setSnackbar,
    mode,
    showViewRolesModal,
    fetchRoles,
}) => {
    const [buttonDisable, setButtonDisable] = useState(false);
    const [loading, setLoading] = useState(false);
    const [modules, setModules] = useState(false);
    const [modulePermission, setModulePermission] = useState(
        roleDataById ? roleDataById.rolePermissions : []
    );
    const [isAdminChecked, setIsAdminChecked] = useState(
        roleDataById ? roleDataById?.isAdmin : false
    );

    const validationSchema = yup.object().shape({
        roleName: yup
            .string()
            .trim()
            .required(VALIDATION.REQUIRED_FIELD("Role name"))
            .min(1, VALIDATION.MIN_LENGTH("Role name", 1))
            .max(20, VALIDATION.MAX_LENGTH("Role name", 20))
            .matches(/^[a-zA-Z0-9\s_\-]+$/, VALIDATION.INVALID_FORMAT("Role name")),
    });

    const formik = useFormik({
        initialValues: {
            roleName: roleDataById?.roleName || "",
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setButtonDisable(true);
            setLoading(true);

            const data = {
                roleName: values.roleName,
                module: modulePermission,
                isAdmin: isAdminChecked,
            };

            try {
                let promise;
                if (roleDataById && roleDataById.id) {
                    promise = await editRoleAction(roleDataById.id, data);
                } else {
                    promise = await createRoleAction(data);
                }

                const res = promise;

                setSnackbar({
                    open: true,
                    message: res.data.message,
                    severity: "success",
                });
                fetchRoles(); // Add this line to refresh the roles
                handleClose();

            } catch (error) {
                setSnackbar({
                    open: true,
                    message: error.response.data.message,
                    severity: "error",
                });
            } finally {
                formik.resetForm();
                setLoading(false);
                setButtonDisable(false);
            }
        },
    });

    const handleRoleNameChange = (e) => {
        formik.setFieldValue(e.target.name, e.target.value.replace(/^\s+/, ''));
        formik.setFieldTouched(e.target.name, true, false);
    };

    const fetchModules = async () => {
        setLoading(true);
        try {
            const res = await getModuleListAction();
            const { data } = res.data;
            setModules(data);

            if (modulePermission.length === 0) {
                const moduleFinalData = data.map((ele) => {
                    let tempModuleObject = {
                        moduleId: ele.id,
                        isAllChecked: false,
                    }; // Add isAllChecked here
                    Object.entries(ele).forEach(([key, value]) => {
                        if (startsWithCan(key) && typeof value === "boolean") {
                            tempModuleObject[key] = false;
                        }
                    });
                    return tempModuleObject;
                });
                setModulePermission(moduleFinalData);
            }
            setLoading(false);
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.response.data.message,
                severity: "error",
            });
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchModules();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startsWithCan = (word) => {
        return word.toLowerCase().startsWith("can");
    };

    const addSpaceBeforeCapital = (str) => {
        let result = "";
        for (let i = 0; i < str.length; i++) {
            if (str[i] === str[i].toUpperCase() && i !== 0) {
                result += " ";
            }
            result += str[i];
        }
        return result;
    };

    const permissionTD = (module) => {
        let moduleData = modulePermission.find((m) => m.moduleId === module.id);

        if (!moduleData) {
            // Initialize default permissions if moduleData is missing
            moduleData = {
                moduleId: module.id,
                isAllChecked: false,
                ...Object.fromEntries(
                    Object.keys(module)
                        .filter((key) => startsWithCan(key))
                        .map((key) => [key, false])
                ),
            };
            setModulePermission((prev) => [...prev, moduleData]);
        }

        const permissionKeys = Object.keys(module).filter(
            (key) => startsWithCan(key) && module[key] === true
        );

        const mismatchedKeys = permissionKeys.filter(
            (key) => moduleData[key] !== true
        );

        const allPermissionsChecked = mismatchedKeys.length === 0;

        moduleData.isAllChecked = allPermissionsChecked;

        return (
            <>
                {/* "All" Checkbox */}
                <td key={`${module.moduleId}-all`} className="text-left">
                    <label className="form-check form-check-sm form-check-custom form-check-solid me-9">
                        <input
                            checked={allPermissionsChecked}
                            onChange={(e) =>
                                handleAllPermissionToggle(
                                    module.id,
                                    e.target.checked
                                )
                            }
                            className="form-check-input"
                            type="checkbox"
                            disabled={showViewRolesModal}
                        />
                        <span className="form-check-label">All</span>
                    </label>
                </td>

                {/* Individual Permission Checkboxes */}
                {Object.entries(module).map(([key, value]) => {
                    if (
                        startsWithCan(key) &&
                        typeof value === "boolean" &&
                        value === true
                    ) {
                        const alteredPermissionName = key.replace("can", "");
                        return (
                            <td key={key} className="text-left">
                                <label className="form-check form-check-sm form-check-custom form-check-solid me-9">
                                    <input
                                        onChange={() =>
                                            handlePermissionToggle(
                                                module.id,
                                                key
                                            )
                                        }
                                        checked={isChecked(module.id, key)}
                                        className="form-check-input"
                                        type="checkbox"
                                        disabled={showViewRolesModal} // Disable in view mode
                                    />
                                    <span className="form-check-label">
                                        {addSpaceBeforeCapital(
                                            alteredPermissionName
                                        )}
                                    </span>
                                </label>
                            </td>
                        );
                    }
                    return null;
                })}
            </>
        );
    };

    const handleAllPermissionToggle = (moduleId, isChecked) => {
        const updatedModules = modulePermission.map((module) => {
            if (module.moduleId === moduleId) {
                const newModule = { ...module };
                Object.keys(newModule).forEach((key) => {
                    if (startsWithCan(key)) {
                        newModule[key] = isChecked; // Toggle each permission
                    }
                });
                newModule.isAllChecked = isChecked; // Set the "All" checkbox
                return newModule;
            }
            return module;
        });

        setModulePermission(updatedModules);
    };

    const handlePermissionToggle = (moduleId, permissionName) => {
        // Find module if it's not in the modulePermission array
        if (modulePermission.findIndex((e) => e.moduleId === moduleId) === -1) {
            const extraData = modules.find((e) => e.id === moduleId);
            const tempModuleObject = {
                moduleId: extraData.id,
                isAllChecked: false,
            };
            Object.entries(extraData).forEach(([key, value]) => {
                if (startsWithCan(key) && typeof value === "boolean") {
                    tempModuleObject[key] = false;
                }
            });
            modulePermission.push(tempModuleObject);
        }

        // Toggle individual permission
        const updatedModules = modulePermission.map((module) => {
            if (module.moduleId === moduleId) {
                module[permissionName] = !module[permissionName];

                // Check if all permissions are enabled after toggling
                const allPermissionsChecked = Object.entries(module)
                    .filter(([key]) => startsWithCan(key))
                    .every(([key, value]) => value === true);

                module.isAllChecked = allPermissionsChecked;
            }
            return module;
        });

        setModulePermission(updatedModules);
    };

    const handleAdministratorAccessPermissionToggle = (e) => {
        setIsAdminChecked(e.target.checked);
        const updatedModules = modulePermission.map((module) => {
            const updatedModule = { ...module };

            // Set all permissions and mark the "All" checkbox as checked
            Object.keys(updatedModule).forEach((key) => {
                if (startsWithCan(key)) {
                    updatedModule[key] = e.target.checked;
                }
            });
            updatedModule.isAllChecked = e.target.checked;

            return updatedModule;
        });

        setModulePermission(updatedModules);
    };

    const isChecked = (moduleId, permissionName) => {
        const moduleData = modulePermission.find(
            (ele) => ele.moduleId === moduleId
        );
        return moduleData ? moduleData[permissionName] : false;
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth={"md"}>
            <DialogTitle className="fw-semibold fs-2 border-bottom">
                {showViewRolesModal ? "View" : roleDataById ? "Update" : "Add"}{" "}
                {"Role"}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: "absolute",
                    right: 8,
                    top: 15,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <MyCloseIcon />
            </IconButton>
            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <div className="mb-5">
                                <label className="fs-5 fw-bold form-label mb-2">
                                    <span className="required">Role name</span>
                                </label>
                                <TextField
                                    placeholder="Enter role name"
                                    name="roleName"
                                    value={formik.values.roleName}
                                    onChange={handleRoleNameChange}
                                    onBlur={formik.handleBlur}
                                    disabled={showViewRolesModal}
                                    error={
                                        formik.touched.roleName &&
                                        Boolean(formik.errors.roleName)
                                    }
                                    helperText={
                                        formik.touched.roleName &&
                                        formik.errors.roleName
                                    }
                                    fullWidth
                                />
                            </div>
                            <div className="mb-10">
                                <label className="fs-5 fw-bold form-label mb-2 me-2">
                                    <span>Is Admin?</span>
                                </label>
                                <input
                                    onChange={(e) =>
                                        handleAdministratorAccessPermissionToggle(
                                            e
                                        )
                                    }
                                    checked={isAdminChecked}
                                    className="form-check-input"
                                    type="checkbox"
                                    disabled={showViewRolesModal}
                                />
                            </div>
                        </Grid>
                        <Grid item xs={12}>
                            <div className="fv-row">
                                <label className="fs-5 fw-bold form-label mb-2">
                                    Role permissions
                                </label>
                                {/* <Grid eventKey="general" title="General"> */}
                                <Grid title="General">
                                    <div className="mb-3 table-responsive">
                                        <table className="table table-nowrap mb-0 align-middle table-row-dashed fs-6 gy-5">
                                            <tbody className="text-gray-600 fw-semibold">
                                                {modules &&
                                                    modules?.map((module) => (
                                                        <tr key={module.id}>
                                                            <td className="text-gray-800">
                                                                {
                                                                    module.moduleName
                                                                }
                                                            </td>
                                                            {permissionTD(
                                                                module
                                                            )}
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </Grid>
                            </div>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <button
                        onClick={handleClose}
                        className="btn btn-outline btn-outline-primary"
                    >
                        Cancel
                    </button>
                    {!showViewRolesModal && (
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={buttonDisable || loading}
                        >
                            {roleDataById ? "Update" : "Add"} {"Role"}
                            {loading ? (
                                <span
                                    className="spinner-border spinner-border-sm ms-2"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                            ) : null}
                        </button>
                    )}
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default AddEditRolesModal;
